import React, { useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, useShowTooltip } from '@modernloop/shared/components';
import { OpenInNewTabIcon } from '@modernloop/shared/icons';
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';

import { Candidate_CandidateFragment } from 'src/generated/mloop-graphql';

import { getCandidateAtsField } from 'src/hooks/atsService/util';

import useIsInExtension from 'src/recruiting-portal/pages/extension/utils/useIsInExtension';

import getCandidateDetailsUrl from 'src/urls/getCandidateDetailsUrl';

import { localizeAnonymizedName } from 'src/utils/anonymization';

type Fragments = {
  candidate: Candidate_CandidateFragment;
};

type Props = {
  applicationId?: string;
  color?: 'inherit' | 'primary';
  variant?: 'subtitle2' | 'body1' | 'h4';
  onClick?: () => void;
};

const Candidate: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidate,
  color = 'inherit',
  variant = 'body1',
  onClick,
}) => {
  const { t } = useTranslation();
  const isInExtension = useIsInExtension();
  const labelRef = useRef<HTMLSpanElement>(null);
  // Anonymized candidates store the literal "Anonymized" in fullName; localize it at render time
  // (keyed off the structured anonymizedAt flag) so non-English sessions don't see English.
  const displayName = localizeAnonymizedName(candidate?.fullName, candidate?.anonymizedAt, t);
  const showLabelTooltip = useShowTooltip(labelRef, displayName);

  const internalCandidateChipRef = useRef<HTMLDivElement>(null);
  const showInternalCandidateChipTooltip = useShowTooltip(
    internalCandidateChipRef,
    t('hiring-dashboard-candidates:candidates.internalCandidate')
  );

  const isInternalCandidate = getCandidateAtsField(candidate?.atsCandidate?.atsFields, 'origin') === 'internal';

  const candidateDetailsUrl = useMemo(() => {
    if (!candidate) {
      return undefined;
    }
    return getCandidateDetailsUrl(candidate.id, { applicationId });
  }, [applicationId, candidate]);

  const handleClick = useCallback(() => {
    if (!onClick) {
      return;
    }
    onClick();
  }, [onClick]);

  const candidateNameJsx = (
    <Tooltip title={showLabelTooltip ? displayName : undefined}>
      <Typography
        fontWeight={variant === 'body1' ? 600 : undefined}
        variant={variant}
        noWrap
        sx={{ minWidth: 0 }}
        ref={labelRef}
      >
        {displayName}
      </Typography>
    </Tooltip>
  );

  // Creating a separate JSX element for the internal candidate chip so that
  // tooltip shows only when the entire text is not visible.
  const internalCandidateLabel = (
    <Tooltip
      title={
        showInternalCandidateChipTooltip ? t('hiring-dashboard-candidates:candidates.internalCandidate') : undefined
      }
    >
      <Typography noWrap ref={internalCandidateChipRef}>
        {t('hiring-dashboard-candidates:candidates.internalCandidate')}
      </Typography>
    </Tooltip>
  );

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box maxWidth={isInternalCandidate ? '60%' : '100%'}>
        <Link
          color={color}
          onClick={handleClick}
          underline="hover"
          target={isInExtension ? '_blank' : undefined}
          rel={isInExtension ? 'noopener noreferrer' : undefined}
          // The name's Typography is block-level; without a flex row here the icon (a sibling)
          // wraps onto its own line below the name instead of sitting inline after it. `gap`
          // spaces the icon instead of the icon's own margin.
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}
          {...{ component: RouterLink, to: candidateDetailsUrl }}
        >
          {candidateNameJsx}
          {/* Narrow condition, on purpose: only show the icon when we know this link opens a new
              tab, i.e. in the extension AND it actually has a destination - matching the
              hasDestination gate in src/components/Link. Showing it elsewhere, or when there is no
              candidateDetailsUrl to open, would be misleading and is an app-wide visual change out
              of scope here. */}
          {isInExtension && candidateDetailsUrl && (
            <OpenInNewTabIcon
              data-testid="candidate-new-tab-icon"
              fontSize="inherit"
              aria-hidden
              style={{ flexShrink: 0 }}
            />
          )}
        </Link>
      </Box>
      {isInternalCandidate && (
        <Chip
          variant="outlined"
          size={variant === 'h4' ? 'medium' : 'small'}
          label={internalCandidateLabel}
          sx={{ backgroundColor: (theme) => theme.palette.background.default, minWidth: 0, maxWidth: '100%' }}
        />
      )}
    </Stack>
  );
};

Candidate.fragments = {
  candidate: gql`
    fragment Candidate_candidate on Candidate {
      id
      fullName
      anonymizedAt
      atsCandidate {
        atsId
        atsFields {
          ... on LeverCandidate {
            origin
          }
        }
      }
    }
  `,
};

export default Candidate;
